import { fetchFileBlob, webDownloadFile, webGetReq } from './api';

// Web API (/api/web/v1), used by the Activity page's Quick Scan tab.

/** The scanned official, with the fields the Activity detail sidebar shows. */
export type ScannedOfficial = {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  img_url: string;
  title: string;
  rank: string;
  badge_number: string;
  verified: boolean;
  phone_number: string | null;
  employment_type: string | null;
  agency: {
    id: number;
    name: string;
  };
};

export type QuickScan = {
  id: number;
  action_type: string;
  identifier: string;
  performed_at: string;
  /** Whether the scanned official was verified at the time of the scan. agency_official.verified is their current status. */
  official_verified: boolean | null;
  agency_official: ScannedOfficial;
  verifier: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
    img_url: string;
  };
};

/** The search and date range, as sent to both the list and the report. */
export type QuickScanFilters = {
  searchText: string;
  /** UTC, e.g. 2026-05-01T06:00:00. Empty for no limit. */
  startDt: string;
  endDt: string;
};

const appendFilterParams = (params: URLSearchParams, { searchText, startDt, endDt }: QuickScanFilters) => {
  if (searchText) params.append('q', searchText);
  if (startDt) params.append('start', startDt);
  if (endDt) params.append('end', endDt);
};

export const fetchQuickScans = async (
  accessToken: string,
  agencyId: number | string,
  { page, pageSize, ...filters }: QuickScanFilters & { page: number; pageSize: number }
) => {
  const params = new URLSearchParams();
  params.append('offset', (page * pageSize).toString());
  params.append('limit', pageSize.toString());
  appendFilterParams(params, filters);
  return webGetReq<QuickScan[]>(`/agencies/${agencyId}/quick_scans?${params}`, accessToken);
};

/** Downloads the matching quick scans as a CSV, with times in the browser's time zone. */
export const downloadQuickScansReport = async (
  accessToken: string,
  agencyId: number | string,
  filters: QuickScanFilters
) => {
  const params = new URLSearchParams();
  appendFilterParams(params, filters);
  params.append('report_tz', Intl.DateTimeFormat().resolvedOptions().timeZone);
  return webDownloadFile(`/agencies/${agencyId}/quick_scans_report?${params}`, accessToken);
};

/**
 * Fetches one batch of the matching quick scans as a PDF, from offset to offset + limit, with
 * page numbers starting after pageOffset. The x-surepass-report-page-offset response header is
 * the page offset for the next batch.
 */
export const fetchQuickScansPdfReportChunk = async (
  accessToken: string,
  agencyId: number | string,
  filters: QuickScanFilters,
  { offset, limit, pageOffset }: { offset: number; limit: number; pageOffset: number }
) => {
  const params = new URLSearchParams();
  params.append('offset', offset.toString());
  params.append('limit', limit.toString());
  if (pageOffset) params.append('report_page_offset', pageOffset.toString());
  appendFilterParams(params, filters);
  params.append('report_format', 'pdf');
  params.append('report_tz', Intl.DateTimeFormat().resolvedOptions().timeZone);
  return fetchFileBlob(`/agencies/${agencyId}/quick_scans_report?${params}`, accessToken, 'web');
};
